import React, { useContext, useMemo } from "react";
import { Link, useLocation, useParams } from "react-router";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  ChipProps,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTextDomainContext } from "@/app/providers";
import { BackButton } from "@/components";
import {
  useCompanies,
  useCountries,
  useLoginAs,
  usePageTitle,
  usePartnersList,
  useRolesList,
  useStatusArray,
  useTenantList,
  useUserProfile,
} from "@/hooks";
import { languageList } from "@/utils";

interface StatusInfo {
  label: string;
  color: ChipProps["color"];
}

interface SystemRole {
  key: string;
  name: string;
}

interface PartnerRole {
  role: string;
  roleName: string;
  partnerName: string;
  partnerId: string;
}

interface CorporateGroupRole {
  role: string;
  roleName: string;
  companyName: string;
  tenantId: string;
}

export const UserProfile = () => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { userId } = useParams();
  const location = useLocation();

  // Set page title
  usePageTitle(gettext("User Profile"));

  // Fetch user profile data
  const { userProfile, loading: loadingProfile } = useUserProfile({ userId });

  // Login as hook
  const { loginAs } = useLoginAs();

  // Fetch reference data
  const { countries } = useCountries();
  const { rolesList } = useRolesList();
  const { customers } = useCompanies({});
  const { tenantsList } = useTenantList({});
  const { partnerList } = usePartnersList();
  const statusArray = useStatusArray();
  const languages = languageList(gettext);

  // Compute back link from current path
  const backLink = useMemo(() => {
    const pathParts = location.pathname.split("/");
    pathParts.pop(); // Remove "profile"
    pathParts.pop(); // Remove userId
    return pathParts.join("/");
  }, [location.pathname]);

  // Compute parent path for navigation
  const parentPath = useMemo(() => {
    const pathParts = location.pathname.split("/");
    return pathParts[1]; // Get the first segment after the leading slash
  }, [location.pathname]);

  // Helper function to get country name from code
  const getCountryName = (countryCode: string) => {
    const country = countries.find((c: any) => c.code === countryCode);
    return country ? country.name : countryCode;
  };

  // Helper function to get language name from code
  const getLanguageName = (langCode: string) => {
    const language = languages.find((l: any) => l.lang === langCode);
    return language ? language.name : langCode?.toUpperCase();
  };

  // Helper function to get role name from key
  const getRoleName = (roleKey: string) => {
    const role = rolesList.find((r: any) => r.key === roleKey);
    return role ? role.label : roleKey;
  };

  // Helper function to get customer name from id
  const getCustomerName = (customerId: string) => {
    const customer = customers.find((c: any) => c.id === customerId);
    return customer ? customer.name : customerId;
  };

  // Helper function to get tenant name from id
  const getTenantName = (tenantId: string) => {
    const tenant = tenantsList.find((t: any) => t.id === tenantId);
    return tenant ? tenant.name : tenantId;
  };

  // Helper function to get partner name from id
  const getPartnerName = (partnerId: string) => {
    const partner = partnerList.find((p: any) => p.id === partnerId);
    return partner ? partner.name : partnerId;
  };

  // Helper function to get status info
  const getStatusInfo = (statusValue: string | undefined): StatusInfo => {
    if (!statusValue) {
      return {
        label: gettext("Not Available"),
        color: "default",
      };
    }
    const status = statusArray.find((s) => s.value === statusValue);
    return status
      ? { label: status.label, color: status.color }
      : { label: statusValue, color: "default" };
  };

  // Get system roles
  const systemRoles = useMemo<SystemRole[]>(() => {
    if (!userProfile?.roles || !Array.isArray(userProfile.roles)) {
      return [];
    }
    return userProfile.roles.map((roleKey: string) => {
      const role = rolesList.find((r: any) => r.key === roleKey);
      return {
        key: roleKey,
        name: role ? role.label : roleKey,
      };
    });
  }, [userProfile, rolesList]);

  // Get partner roles
  const partnerRoles = useMemo<PartnerRole[]>(() => {
    if (!userProfile?.email || !partnerList) {
      return [];
    }
    const roles: PartnerRole[] = [];
    partnerList.forEach((partner) => {
      if (partner.users && Array.isArray(partner.users)) {
        const userInPartner = partner.users.find(
          (u) => u.userId === userProfile.id || u.userId === userProfile.email,
        );
        if (userInPartner) {
          const role = rolesList.find((r) => r.key === userInPartner.role);
          roles.push({
            role: userInPartner.role,
            roleName: role ? role.label : userInPartner.role,
            partnerName: partner.name,
            partnerId: partner.id,
          });
        }
      }
    });
    return roles;
  }, [userProfile, partnerList, rolesList]);

  // Get corporate group (tenant) roles
  const corporateGroupRoles = useMemo<CorporateGroupRole[]>(() => {
    if (!userProfile?.email || !tenantsList) {
      return [];
    }
    const roles: CorporateGroupRole[] = [];
    tenantsList.forEach((tenant: any) => {
      if (tenant.users && Array.isArray(tenant.users)) {
        const userInTenant = tenant.users.find(
          (u: any) =>
            u.userId === userProfile.id || u.userId === userProfile.email,
        );
        if (userInTenant) {
          const role = rolesList.find((r) => r.key === userInTenant.role);
          roles.push({
            role: userInTenant.role,
            roleName: role ? role.label : userInTenant.role,
            companyName: tenant.name,
            tenantId: tenant.id,
          });
        }
      }
    });
    return roles;
  }, [userProfile, tenantsList, rolesList]);

  if (loadingProfile) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!userProfile) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">{gettext("User not found")}</Typography>
        {backLink && <BackButton link={backLink} />}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 3, alignItems: "center", justifyContent: "space-between" }}
      >
        <Typography variant="h4">{gettext("User Profile")}</Typography>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          {backLink && <BackButton link={backLink} />}
          <Button
            variant="contained"
            onClick={() => loginAs(userProfile.email)}
          >
            {gettext("Login As")}
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        {/* Basic Profile Information */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {gettext("Profile")}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                {userProfile.profilePhotoUrl && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                  >
                    <Avatar
                      src={userProfile.profilePhotoUrl}
                      alt={userProfile.name}
                      sx={{ width: 120, height: 120 }}
                    />
                  </Box>
                )}

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {gettext("Name")}:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="body1">
                      {userProfile.name || "-"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {gettext("Email")}:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="body1">
                      {userProfile.email || "-"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {gettext("Language")}:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="body1">
                      {userProfile.lang
                        ? getLanguageName(userProfile.lang)
                        : "-"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {gettext("Country")}:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="body1">
                      {userProfile.countryCode
                        ? getCountryName(userProfile.countryCode)
                        : "-"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {gettext("Active Role")}:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="body1">
                      {userProfile.currentRoleKey
                        ? getRoleName(userProfile.currentRoleKey)
                        : "-"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {gettext("Active Parent Company")}:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="body1">
                      {userProfile.currentTeamId
                        ? getCustomerName(userProfile.currentTeamId)
                        : "-"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {gettext("Active Child Company")}:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="body1">
                      {userProfile.currentSubsidiaryId
                        ? getCustomerName(userProfile.currentSubsidiaryId)
                        : "-"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {gettext("Active Corporate Group")}:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    {userProfile.currentTenantId ? (
                      <Link
                        to={`/${parentPath}/tenants/${userProfile.currentTenantId}/companies`}
                        style={{ textDecoration: "none" }}
                        state={{ parentPath: location.pathname }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                            color: "primary",
                          }}
                        >
                          {getTenantName(userProfile.currentTenantId)}
                        </Typography>
                      </Link>
                    ) : (
                      <Typography variant="body1">-</Typography>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {gettext("Active Partner")}:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    {userProfile.currentPartnerId ? (
                      <Link
                        to={`/${parentPath}/partners/${userProfile.currentPartnerId}/tenants`}
                        style={{ textDecoration: "none" }}
                        state={{ parentPath: location.pathname }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                            color: "primary",
                          }}
                        >
                          {getPartnerName(userProfile.currentPartnerId)}
                        </Typography>
                      </Link>
                    ) : (
                      <Typography variant="body1">-</Typography>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {gettext("Status")}:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Chip
                      label={getStatusInfo(userProfile.status).label}
                      color={getStatusInfo(userProfile.status).color}
                      size="small"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {gettext("Last Login")}:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="body1">
                      {userProfile.lastLogin
                        ? new Date(userProfile.lastLogin).toLocaleString()
                        : "-"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {gettext("Created At")}:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="body1">
                      {userProfile.createdAt
                        ? new Date(userProfile.createdAt).toLocaleString()
                        : "-"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {gettext("Updated At")}:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="body1">
                      {userProfile.updatedAt
                        ? new Date(userProfile.updatedAt).toLocaleString()
                        : "-"}
                    </Typography>
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Roles Section */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            {/* System Roles */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {gettext("System Roles")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {systemRoles.length > 0 ? (
                  <Stack spacing={1}>
                    {systemRoles.map((role, index) => (
                      <Chip
                        key={index}
                        label={role.name}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {gettext("No system roles assigned")}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Partner Roles */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {gettext("Partner Roles")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {partnerRoles.length > 0 ? (
                  <Stack spacing={2}>
                    {partnerRoles.map((role, index) => (
                      <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {role.roleName}
                        </Typography>
                        <Link
                          to={`/${parentPath}/partners/${role.partnerId}/tenants`}
                          style={{ textDecoration: "none" }}
                          state={{ parentPath: location.pathname }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                              color: "primary",
                            }}
                          >
                            {role.partnerName}
                          </Typography>
                        </Link>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {gettext("No partner roles assigned")}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Corporate Group Roles */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {gettext("Corporate Group Roles")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {corporateGroupRoles.length > 0 ? (
                  <Stack spacing={2}>
                    {corporateGroupRoles.map((role, index) => (
                      <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {role.roleName}
                        </Typography>
                        <Link
                          to={`/${parentPath}/tenants/${role.tenantId}/companies`}
                          style={{ textDecoration: "none" }}
                          state={{ parentPath: location.pathname }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                              color: "primary",
                            }}
                          >
                            {role.companyName}
                          </Typography>
                        </Link>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {gettext("No corporate group roles assigned")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
